import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { usePendingOrdersCount } from '@/hooks/usePendingOrdersCount';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { useUserOrganizationRole } from '@/hooks/useUserRole';
import { getUserRole } from '@/lib/auth/token';
import {
  BellRing,
  Building2,
  CreditCard,
  FileText,
  Globe,
  Home,
  LineChart,
  Package,
  Plane,
  ReceiptEuro,
  RefreshCw,
  Shield,
  Smartphone,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { EikoLogo } from './EikoLogo';
import { OrganizationName } from './OrganizationName';
import { OrganizationSwitcher } from './OrganizationSwitcher';

export function AppSidebar() {
  const { tenantSlug } = useTenantSlug();
  const location = useLocation();
  const userRole = getUserRole();
  const userOrganizationRole = useUserOrganizationRole();
  const canSeeSyncMonitoring = userRole === 'ADMIN';
  const { data: pendingOrdersCount = 0 } = usePendingOrdersCount();

  const isViewer = userOrganizationRole === 'VIEWER';
  const isRouteActive = (pattern: RegExp) => pattern.test(location.pathname);

  return (
    <Sidebar collapsible="offcanvas">
      {/* Header — Logo with emerald gradient */}
      <SidebarHeader className="pb-0">
        <NavLink to={`/o/${tenantSlug}/home`} className="flex items-center justify-center px-4 py-3">
          <EikoLogo className="h-8 text-emerald-600" />
        </NavLink>
        {userRole === 'ADMIN' && (
          <div className="px-2 pt-2 pb-2">
            <OrganizationSwitcher />
          </div>
        )}
        <div className="px-2 pb-2 sm:hidden">
          <OrganizationName />
        </div>
      </SidebarHeader>

      <SidebarContent className="pt-2">
        {/* Accueil */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(/^\/o\/[^/]+\/home(?:\/.*)?$/)}
                  tooltip="Accueil"
                >
                  <NavLink to={`/o/${tenantSlug}/home`}>
                    <Home />
                    <span>Accueil</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Mon parc */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-widest text-emerald-600/70 font-semibold px-2.5 py-0.5 bg-emerald-50/80 rounded-full w-fit">
            Mon parc
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(/^\/o\/[^/]+\/lines(?:\/.*)?$/)}
                  tooltip="Mes lignes"
                >
                  <NavLink to={`/o/${tenantSlug}/lines`}>
                    <LineChart />
                    <span>Mes lignes</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(
                    /^\/o\/[^/]+\/travel-esims(?:\/.*)?$/,
                  )}
                  tooltip="eSIM internationales"
                >
                  <NavLink to={`/o/${tenantSlug}/travel-esims`}>
                    <Globe />
                    <span>eSIM internationales</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(/^\/o\/[^/]+\/sim-stock(?:\/.*)?$/)}
                  tooltip="Mon stock de SIM"
                >
                  <NavLink to={`/o/${tenantSlug}/sim-stock`}>
                    <CreditCard />
                    <span>Mon stock de SIM</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(
                    /^\/o\/[^/]+\/consumption-alerts(?:\/.*)?$/,
                  )}
                  tooltip="Alertes de consommation"
                >
                  <NavLink to={`/o/${tenantSlug}/consumption-alerts`}>
                    <BellRing />
                    <span>Alertes de consommation</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Commercial */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-widest text-emerald-600/70 font-semibold px-2.5 py-0.5 bg-emerald-50/80 rounded-full w-fit">
            Commercial
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(/^\/o\/[^/]+\/orders(?:\/.*)?$/)}
                  tooltip="Commandes"
                >
                  <NavLink to={`/o/${tenantSlug}/orders`}>
                    <Package />
                    <span>Commandes</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isRouteActive(/^\/o\/[^/]+\/invoices(?:\/.*)?$/)}
                  tooltip="Factures"
                >
                  <NavLink to={`/o/${tenantSlug}/invoices`}>
                    <FileText />
                    <span>Factures</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {!isViewer && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isRouteActive(/^\/o\/[^/]+\/catalog(?:\/.*)?$/)}
                    tooltip="Catalogue"
                  >
                    <NavLink to={`/o/${tenantSlug}/catalog`}>
                      <Smartphone />
                      <span>Catalogue</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin */}
        {userRole === 'ADMIN' && (
          <>
            <SidebarGroup className="pt-4 mt-2">
              <SidebarGroupLabel className="text-[11px] uppercase tracking-widest text-amber-600/70 font-semibold px-2.5 py-0.5 bg-amber-50/80 rounded-full w-fit">
                <Shield className="mr-1.5 h-3 w-3" />
                Admin
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isRouteActive(/^\/o\/?$/)}
                      tooltip="Toutes les organisations"
                    >
                      <NavLink to="/o/">
                        <Building2 />
                        <span>Toutes les organisations</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isRouteActive(
                        /^\/o\/admin\/onboarding(?:\/.*)?$/,
                      )}
                      tooltip="Onboarding"
                    >
                      <NavLink to="/o/admin/onboarding">
                        <Plane />
                        <span>Onboarding</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isRouteActive(/^\/o\/admin\/orders(?:\/.*)?$/)}
                      tooltip="Commandes entrantes"
                    >
                      <NavLink to="/o/admin/orders">
                        <Package />
                        <span>Commandes entrantes</span>
                      </NavLink>
                    </SidebarMenuButton>
                    {pendingOrdersCount > 0 && (
                      <SidebarMenuBadge className="bg-gradient-to-r from-rose-500 to-pink-500 !text-white text-[10px] font-semibold rounded-full shadow-md shadow-rose-500/25">
                        {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                      </SidebarMenuBadge>
                    )}
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isRouteActive(/^\/o\/admin\/catalog(?:\/.*)?$/)}
                      tooltip="Catalogue admin"
                    >
                      <NavLink to="/o/admin/catalog">
                        <Smartphone />
                        <span>Catalogue admin</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {canSeeSyncMonitoring && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isRouteActive(/^\/o\/admin\/sync(?:\/.*)?$/)}
                        tooltip="Synchronisation"
                      >
                        <NavLink to="/o/admin/sync">
                          <RefreshCw />
                          <span>Synchronisation</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {canSeeSyncMonitoring && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isRouteActive(
                          /^\/o\/admin\/billing(?:\/.*)?$/,
                        )}
                        tooltip="Facturation"
                      >
                        <NavLink to="/o/admin/billing">
                          <ReceiptEuro />
                          <span>Facturation</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter />
    </Sidebar>
  );
}
